import RestCrudThunks from './restCrudThunksGenerator';
import { SUPPLIER_FACILITIES_CRUD_ACTION_CREATORS } from '../actions/supplier_facilities_actions';
import { ROLE_TYPES } from '../utils/DataConstants';
import { default as FetchUtils, parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { toBackendDateString } from './kpi_comparison_thunks';

const fetchUtils = new FetchUtils();

export const supplierScorecardsRestCrudThunksForBuyer = new RestCrudThunks(
	'kpi/supplier_scorecards',
	'kpi/supplier_scorecard',
	'supplierScorecard',
	'supplierScorecards',
	SUPPLIER_FACILITIES_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER
);

export const supplierScorecardsRestCrudThunksForSupplier = new RestCrudThunks(
	'kpi/supplier_scorecards',
	'kpi/supplier_scorecard',
	'supplierScorecard',
	'supplierScorecards',
	SUPPLIER_FACILITIES_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.SUPPLIER
);

export const supplierScorecardsRestCrudThunks = (userType) =>
	userType === ROLE_TYPES.SUPPLIER
		? supplierScorecardsRestCrudThunksForSupplier
		: supplierScorecardsRestCrudThunksForBuyer;

export const SCORECARD_MONTHLY_ENDPOINTS = {
	workOrders: 'monthly_work_orders',
	responseTime: 'monthly_response_time',
	resolutionTime: 'monthly_resolution_time',
	firstTimeFix: 'monthly_first_time_fix_rate',
	spend: 'monthly_spend',
	checkIn: 'monthly_check_in',
	completedOnTime: 'monthly_completed_on_time',
	invoicedWithin30Days: 'monthly_invoiced_within_30_days',
};

const JSON_HEADERS = { Accept: 'application/json', 'Content-Type': 'application/json' };

const scorecardUrl = (userType, path) =>
	getBackendUri() + '/api/v1/' + userType + '/kpi/supplier_scorecards/' + path;

const SCORECARD_LIST_PAGE_SIZE = 10000;

export const fetchSupplierScorecardsList =
	(userType) =>
	(params): Promise<any[]> => {
		const queryParams = Object.assign({ offset: 0, limit: SCORECARD_LIST_PAGE_SIZE }, params);
		if (queryParams.woPriorityId !== null && queryParams.woPriorityId !== undefined) {
			queryParams.woPriorityIds = queryParams.woPriorityId;
		}
		if (queryParams.notWoPriorityId !== null && queryParams.notWoPriorityId !== undefined) {
			queryParams.notWoPriorityIds = queryParams.notWoPriorityId;
		}
		delete queryParams.woPriorityId;
		delete queryParams.notWoPriorityId;
		return fetchUtils
			.get(
				getBackendUri() + '/api/v1/' + userType + '/kpi/supplier_scorecards',
				Object.assign({ headers: JSON_HEADERS }, queryParams)
			)
			.then(parseJSON)
			.then(({ json, ok }) => {
				if (!ok || (json && json.status === 'error')) {
					return Promise.reject((json && json.message) || 'Failed to load supplier scorecards.');
				}
				return ((json && json.data) || []).map((row) => ({
					...row,
					supplierFacilityName: row.displayName || row.name,
					woCount: row.completedWos,
				}));
			});
	};

export const fetchSupplierScorecardMonthly =
	(userType) =>
	(endpoint: string) =>
	(params): Promise<any[]> =>
		new Promise((resolve) => {
			fetchUtils
				.get(scorecardUrl(userType, endpoint), Object.assign({ headers: JSON_HEADERS }, params))
				.then(parseJSON)
				.then(({ json, ok }) => {
					if (!ok || (json && json.status === 'error')) {
						resolve([]);
						return;
					}
					resolve((json && json.data) || []);
				})
				.catch(() => resolve([]));
		});

export const fetchSupplierScorecardComparison =
	(endpoint: string) =>
	(userType) =>
	(params): Promise<any[]> => {
		const createdAtStartDate = toBackendDateString(params && params.startDate);
		const createdAtEndDate = toBackendDateString(params && params.endDate);
		if (!createdAtStartDate || !createdAtEndDate) {
			return Promise.resolve([]);
		}
		const queryParams = Object.assign({}, params, { createdAtStartDate, createdAtEndDate });
		delete queryParams.startDate;
		delete queryParams.endDate;
		return new Promise((resolve) => {
			fetchUtils
				.get(
					scorecardUrl(userType, endpoint),
					Object.assign({ headers: JSON_HEADERS }, queryParams)
				)
				.then(parseJSON)
				.then(({ json, ok }) => {
					if (!ok || (json && json.status === 'error')) {
						resolve([]);
						return;
					}
					resolve(
						((json && json.data) || []).filter(
							(row) => !row.isPreviousPeriod && !row.isPreviousYear
						)
					);
				})
				.catch(() => resolve([]));
		});
	};

export const fetchSupplierScorecardSummary =
	(userType) =>
	(params): Promise<any> =>
		new Promise((resolve) => {
			fetchUtils
				.get(scorecardUrl(userType, 'summary'), Object.assign({ headers: JSON_HEADERS }, params))
				.then(parseJSON)
				.then(({ json, ok }) => {
					if (!ok || (json && json.status === 'error')) {
						resolve(null);
						return;
					}
					resolve((json && json.data) || null);
				})
				.catch(() => resolve(null));
		});
